import "nprogress/nprogress.css";
import "~/styles/globals.scss";

import { ContentfulLivePreviewProvider } from "@contentful/live-preview/react";
import {
  Hydrate,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { SpeedInsights } from "@vercel/speed-insights/next";
import type { AppProps } from "next/app";
import localFont from "next/font/local";
import Router, { useRouter } from "next/router";
import {
  AbstractIntlMessages,
  IntlErrorCode,
  NextIntlClientProvider,
} from "next-intl";
import nProgress from "nprogress";
import { useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { RecoilRoot } from "recoil";

import CountryDetectModal from "~/components/common/country-detect-modal/country-detect-modal";
import GenericError from "~/components/common/generic-error";
import PreviewModeBar from "~/components/layout/preview-mode-bar";
import UsableNetIntegration from "~/components/layout/usable-net-integration";
import BreakpointProvider from "~/contexts/breakpoint";
import { PageTrackingsProvider } from "~/contexts/pagetrackings";
import { ServerSidePropsProvider } from "~/contexts/serverSideProps";
import TextDirProvider from "~/contexts/text-dir";
import UserConsentsProvider from "~/contexts/userconsents";
import Logger from "~/services/logger/logger";
import CommonUtils from "~/utils/common-utils";
import Constants from "~/utils/constants";
import contentfulUtils from "~/utils/contentful-utils";

nProgress.configure({
  showSpinner: false,
  minimum: 0.1,
  speed: 300,
  trickleSpeed: 100,
});
Router.events.on("routeChangeStart", nProgress.start);
Router.events.on("routeChangeError", nProgress.done);
Router.events.on("routeChangeComplete", nProgress.done);

type Props = {
  organizationUnitId: number;
  messages?: AbstractIntlMessages;
  dehydratedState?: unknown;
  draftMode?: boolean;
  timeZones: string[];
};

const font = localFont({
  src: [
    {
      path: "../../public/font-thin.woff2",
      weight: "100",
    },
    {
      path: "../../public/font-extralight.woff2",
      weight: "200",
    },
    {
      path: "../../public/font-light.woff2",
      weight: "300",
    },
    {
      path: "../../public/font-regular.woff2",
      weight: "400",
    },
    {
      path: "../../public/font-semibold.woff2",
      weight: "600",
    },
    {
      path: "../../public/font-bold.woff2",
      weight: "700",
    },
  ],
});

function onError(error: { code: any }) {
  if (error.code === IntlErrorCode.MISSING_MESSAGE) {
    // Missing translations are expected and should only log an error
    console.warn(error);
  } else {
    // Other errors indicate a bug in the app and should be reported
    console.error(error);
  }
}

export default function App({ Component, pageProps }: AppProps<Props>) {
  const router = useRouter();
  const [queryClient] = useState(new QueryClient({}));
  const [forceRefresh, setForceRefresh] = useState<number>(0);

  const [lang, country] = router.locale?.split("-") ?? ["en", "ae"];

  // to debug route changes
  useEffect(() => {
    const handleRouteChange = (url: string) => {
      Logger.instance.debug("Router: routeChangeStart to: ", url);
    };

    router.events.on("routeChangeStart", handleRouteChange);
    return () => {
      router.events.off("routeChangeStart", handleRouteChange);
    };
  }, [router]);

  useEffect(() => {
    // Update the current `lang` and `dir` for the html tag dynamically.
    const html = document.querySelector("html")!;

    const dir = lang === "ar" ? "rtl" : "ltr";

    html.setAttribute("lang", lang);
    html.setAttribute("dir", dir);

    CommonUtils.isWindows() &&
      document.body.classList.add(Constants.SCROLLBAR_CLASSNAME);
  }, [country, lang, router.locale]);

  // This hook clears the shopping cart in local storage and updates the last visited country when the user's country changes.
  useEffect(() => {
    if (country !== localStorage.getItem("lastVisitedCountry")) {
      localStorage.setItem("lastVisitedCountry", country);
      const localStorageKeysLength: number = localStorage.length;
      for (let i: number = 0; i < localStorageKeysLength; i++) {
        const localStorageKey = localStorage.key(i);
        if (
          localStorageKey?.includes(
            Constants.SHOPPINGCART_LOCALSTORAGE_KEY_SUBSTRING
          )
        ) {
          localStorage.removeItem(localStorageKey);
        }
      }
      setForceRefresh(forceRefresh + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);

  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/next-service-worker.js");
    }
  }, []);

  return (
    <>
      <style jsx global>{`
        :root {
          --local-font: ${font.style.fontFamily};
        }
      `}</style>
      <RecoilRoot>
        <NextIntlClientProvider
          locale={router.locale}
          timeZone={pageProps.timeZones?.[0]}
          messages={router.query.debugLabels ? {} : pageProps.messages}
          onError={onError}
        >
          <ErrorBoundary fallbackRender={() => <GenericError {...pageProps} />}>
            <BreakpointProvider>
              <TextDirProvider>
                <ServerSidePropsProvider serverSideProps={pageProps}>
                  <ContentfulLivePreviewProvider
                    locale={contentfulUtils.mapLocale(router.locale)}
                    enableInspectorMode={pageProps.draftMode ?? false}
                    enableLiveUpdates={pageProps.draftMode ?? false}
                    debugMode={process.env.NODE_ENV !== "production"}
                  >
                    <QueryClientProvider client={queryClient}>
                      <Hydrate state={pageProps.dehydratedState}>
                        <UserConsentsProvider>
                          <PageTrackingsProvider>
                            <PreviewModeBar />
                            <UsableNetIntegration />
                            {CommonUtils.parseBoolean(
                              process.env.NEXT_PUBLIC_ENABLE_COUNTRY_DETECT
                            ) && <CountryDetectModal />}
                            <Component {...pageProps} />
                          </PageTrackingsProvider>
                        </UserConsentsProvider>
                      </Hydrate>
                      <ReactQueryDevtools
                        initialIsOpen={false}
                        position={
                          lang === "ar" ? "bottom-left" : "bottom-right"
                        }
                      />
                    </QueryClientProvider>
                  </ContentfulLivePreviewProvider>
                </ServerSidePropsProvider>
              </TextDirProvider>
            </BreakpointProvider>
          </ErrorBoundary>
        </NextIntlClientProvider>
      </RecoilRoot>
      <SpeedInsights />
    </>
  );
}
